import { store } from '@redux/store'
import { t } from './translation'
import { AUTH } from '@const'
import { checkValid } from './auth'
import { getPfCurrency } from './currency'
import * as utils from '@utils'
/**
 * @param {object} rootRoleInfo 
 * @param {number} rootRoleId
 * rootRoleId = 2 && rootRoleInfo = null => 最高權限
 * rootRoleId = 2 && rootRoleInfo 有內容 => 一般管理員
 * 新增角色(rootRoleId = 3 || && rootRoleInfo = null), 編輯角色(rootRoleId > 2 && rootRoleInfo = null) => 總代理
 * rootRoleId > 3 && rootRoleInfo 有內容 => 子代理
 */
export function generateAuthTreeData(rootRoleInfo, rootRoleId) {
    const treeData = []
    const auth = store.getState().auth

    if (!auth.userInfo) return treeData

    //Note: only support two-level
    let components = Object.assign({}, AUTH.STANDARD_AUTH)

    if (auth.isSuperUser) {
        components = Object.assign(components, AUTH.SUPER_USER_AUTH)
    }
    /** 由前端暫時移除權限表 */
    rootRoleInfo && utils.tempRemoveAuthorization(rootRoleInfo.authorization_list)
    // filter invalid auth action
    const tempComp = {}
    Object.entries(components).forEach(([key, arr]) => {
        const tempArr = []
        arr.forEach((elem) => {
            const authKey = `${key}.${elem}`

            /** 子層角色, 依照上層角色權限篩選 */
            if (rootRoleInfo && !rootRoleInfo['authorization_list'].includes(authKey)) {
                return
            }
            
            /** 一般管理者(未登入超級權限帳號), 不可顯示之權限 */
            if (rootRoleInfo && (rootRoleId === 2 && !auth.isSuperUser) &&
                (
                    authKey.startsWith('user.')  ||
                    authKey.startsWith('role.')
                )
            )   { return }

            /** 子代理, 不可顯示之權限 */
            /** 子代理特例清單 */
            const specialCaseList = ['user.view','user.reset_pwd','user.view_peers']
            if (rootRoleInfo && rootRoleId > 3 &&
                (
                    (authKey.startsWith('user.') && !specialCaseList.includes(authKey)) ||
                    authKey.startsWith('role.')
                )
            ) { return }

            /** 總代理/代理, 不可顯示之權限 */
            if (rootRoleId > 2 && 
                (
                    authKey.startsWith('announcement.') || 
                    authKey.startsWith('marquee.') || 
                    authKey.startsWith('finance_reconciliation.') || 
                    authKey.startsWith('game_list.release') || 
                    authKey.startsWith('role.administrator') || 
                    authKey.startsWith('report.maker') ||
                    authKey.startsWith('user.otp') ||
                    authKey.startsWith('user.admin') ||
                    authKey.startsWith('user.otp_active') ||
                    authKey.startsWith('internal.validate') ||
                    authKey.startsWith('game_list.exclusive') ||
                    authKey.startsWith('event_prize.edit')
                )
            )   { return }

            if (!checkValid(authKey)) return

            tempArr.push(elem)
        })

        if (tempArr.length > 0) tempComp[key] = tempArr
    })
    components = tempComp

    for (const comID in components) {
        const obj = {
            title: t('Component.' + comID),
            key: comID,
            children: [],
        }

        for (const idx in components[comID]) {
            const action = components[comID][idx]

            obj.children.push({
                title: t(`AuthAction.${action}`),
                key: comID + '.' + action,
            })
        }

        // TODO: support multi-level
        if (!comID.includes('/')) {
            treeData.push(obj)
        } else {
            const nestComp = comID.split('/')

            for (const idx in treeData) {
                if (treeData[idx].key === nestComp[0]) {
                    treeData[idx].children.push(obj)

                    break
                }
            }
        }
    }
    
    return treeData
}
/**
 * 
 * @param {Boolean} showAllPlatform 是否顯示所有平台(包含開啟/關閉)
 * @returns 
 */
export function generatePfTreeData(rootRoleInfo, filterPfSubPfIDList, showAllPlatform) {
    let treeData = []
    const dataService = store.getState().dataService

    if (!dataService.pfSubPfMap) return treeData

    Object.entries(dataService.pfSubPfMap).forEach(([pf_sub_pf_id, value]) => {
        if (!value.is_display && !showAllPlatform) { return } /** 關閉的平台不顯示 */
        // if (!value.is_active && !showAllPlatform) { return } /** 關閉的平台不顯示 */
        if (filterPfSubPfIDList?.includes(pf_sub_pf_id)) return
        if (rootRoleInfo?.['pf_sub_pf_id_list']?.length > 0 && !rootRoleInfo['pf_sub_pf_id_list'].includes(pf_sub_pf_id)) return

        if (undefined === treeData.find((node) => node.key === value.pf_id)) {
            treeData.push({
                title: `${t('Platform.' + value.pf_id)} (${t('Currency.' + getPfCurrency(value.pf_id))})`,
                key: value.pf_id,
                value: value.pf_id,
                children: [],
            })
        }

        treeData[treeData.findIndex((node) => node.key === value.pf_id)].children.push({
            title: value.name,
            key: value.pf_sub_pf_id,
            value: value.pf_sub_pf_id,
        })
    })
    /** 平台排序 by title */
    treeData.forEach((group)=> {
        group.children = utils.convertSort({ source: group.children, sortKey: 'title' })
    })
    /** 站台(幣別)排序 by key */
    treeData = utils.sortPlatform(treeData, 'key')
    return treeData
}

export function generateGameTreeData(slotOnly, openGame) {
    const treeData = []
    const dataService = store.getState().dataService

    if (!dataService.gameList) return treeData

    dataService.gameList.forEach((elem) => {
        const displayGameTypeCode = elem['display_game_type_code_id'],
              gameCode = elem['game_code']
              
        if (openGame && openGame.length) {
            if (!openGame.includes(gameCode)) { return } 
            if (undefined === treeData.find((node) => node.key === displayGameTypeCode)) {
                treeData.push({
                    title: `${t(`GameType.${displayGameTypeCode}`)}`,
                    key: displayGameTypeCode,
                    value: displayGameTypeCode,
                    children: [],
                })
            }
            if(String(elem.maker) === '0' && openGame.includes(gameCode)){
                treeData[treeData.findIndex((node) => node.key === displayGameTypeCode)].children.push({
                    title: t(`GameName.${elem['game_code']}`),
                    key: gameCode,
                    value: gameCode,
                })
            }
        } else if (true === slotOnly && '2' !== displayGameTypeCode) {
            // Do nothing
        } else {
            if (undefined === treeData.find((node) => node.key === displayGameTypeCode)) {
                treeData.push({
                    title: `${t(`GameType.${displayGameTypeCode}`)}`,
                    key: displayGameTypeCode,
                    value: displayGameTypeCode,
                    children: [],
                })
            }

            // treeData[treeData.findIndex((node) => node.key === displayGameTypeCode)].children.push({
            //     title: t(`GameName.${elem['game_code']}`),
            //     key: gameCode,
            //     value: gameCode,
            // })

            if(String(elem.maker) === '0'){
                treeData[treeData.findIndex((node) => node.key === displayGameTypeCode)].children.push({
                    title: t(`GameName.${elem['game_code']}`),
                    key: gameCode,
                    value: gameCode,
                })
            }
        }
    })

    treeData.sort(function(a, b) {
        return a.key.localeCompare(b.key)
    })

    return treeData
}

export function getTreeAllKeys(treeData, keys) {
    if (!keys) keys = []

    treeData.forEach((elem) => {
        keys.push(elem.key)
        if (elem.children) getTreeAllKeys(elem.children, keys)
    })

    return keys
}